import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Smartphone, Download, Share, Plus, CheckCircle,
  ArrowLeft, Zap, Bell, Wifi, Globe, ChevronRight
} from 'lucide-react';

export default function InstallPage() {
  const navigate = useNavigate();
  const [deviceType, setDeviceType] = useState(() => {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'ios';
    } else if (/android/i.test(userAgent)) {
      return 'android';
    }
    return 'desktop';
  });
  const [isInstalled, setIsInstalled] = useState(() => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    { icon: Zap, title: 'Accès instantané', desc: 'Lancez l\'app depuis votre écran d\'accueil' },
    { icon: Bell, title: 'Notifications', desc: 'Recevez des alertes en temps réel' },
    { icon: Wifi, title: 'Mode hors-ligne', desc: 'Consultez vos commandes sans internet' },
    { icon: Globe, title: 'Pas de téléchargement', desc: 'Installation légère et rapide' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E40AF] via-[#1E3A8A] to-[#172554]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/10 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-[#FCD34D] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Retour</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/icons/icon-72x72.png" alt="ARTEKNOS" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">ARTEKNOS</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-white shadow-2xl mb-6">
            <img src="/icons/icon-96x96.png" alt="ARTEKNOS" className="w-20 h-20 rounded-2xl" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
            Installez ARTEKNOS
          </h1>
          <p className="text-lg text-blue-200 max-w-md mx-auto">
            Accédez à Livraison Express directement depuis votre écran d&apos;accueil
          </p>

          {isInstalled && (
            <div className="mt-6 inline-flex items-center gap-2 bg-green-500/20 text-green-300 px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5" />
              <span>Application déjà installée!</span>
            </div>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 gap-4 mb-12">
          {features.map((feature) => (
            <Card key={feature.title} className="bg-white/10 border-white/10 backdrop-blur">
              <CardContent className="p-4 text-center">
                <feature.icon className="w-8 h-8 text-[#FCD34D] mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm mb-1">{feature.title}</h3>
                <p className="text-blue-200 text-xs">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Installation Instructions */}
        <Card className="bg-white shadow-xl mb-8">
          <CardContent className="p-6">
            {/* Device Tabs */}
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-full">
              {[
                { id: 'android', label: 'Android', icon: '🤖' },
                { id: 'ios', label: 'iPhone/iPad', icon: '🍎' },
                { id: 'desktop', label: 'Ordinateur', icon: '💻' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDeviceType(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                    deviceType === tab.id
                      ? 'bg-[#1E40AF] text-white shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Android Instructions */}
            {deviceType === 'android' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Installation sur Android</h3>

                {deferredPrompt ? (
                  <div className="text-center py-6">
                    <Button
                      onClick={handleInstall}
                      className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-full px-8 py-6 text-lg font-semibold"
                    >
                      <Download className="w-6 h-6 mr-2" />
                      Installer maintenant
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-gray-600">Avec Chrome ou Edge :</p>
                    <div className="space-y-4">
                      {[
                        { step: 1, text: 'Appuyez sur le menu ⋮ (3 points) en haut à droite' },
                        { step: 2, text: 'Sélectionnez "Installer l\'application" ou "Ajouter à l\'écran d\'accueil"' },
                        { step: 3, text: 'Confirmez en appuyant sur "Installer"' },
                      ].map((item) => (
                        <div key={item.step} className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-[#1E40AF] text-white flex items-center justify-center font-bold flex-shrink-0">
                            {item.step}
                          </div>
                          <p className="text-gray-700 pt-2">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* iOS Instructions */}
            {deviceType === 'ios' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Installation sur iPhone / iPad</h3>
                <p className="text-gray-600">Avec Safari uniquement :</p>

                <div className="space-y-4">
                  {[
                    { step: 1, icon: <Share className="w-6 h-6 text-[#1E40AF]" />, text: 'Appuyez sur le bouton Partager' },
                    { step: 2, icon: <Plus className="w-6 h-6 text-[#1E40AF]" />, text: 'Faites défiler et appuyez sur "Sur l\'écran d\'accueil"' },
                    { step: 3, icon: <CheckCircle className="w-6 h-6 text-green-500" />, text: 'Appuyez sur "Ajouter" en haut à droite' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-[#1E40AF] text-white flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex items-center gap-3 pt-2">
                        {item.icon}
                        <p className="text-gray-700">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>⚠️ Important :</strong> L&apos;installation ne fonctionne qu&apos;avec Safari.
                    Si vous utilisez Chrome ou autre navigateur, ouvrez d&apos;abord cette page dans Safari.
                  </p>
                </div>
              </div>
            )}

            {/* Desktop Instructions */}
            {deviceType === 'desktop' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Installation sur ordinateur</h3>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-semibold text-gray-900 mb-2">Chrome / Edge</h4>
                    <p className="text-gray-600 text-sm">
                      Cliquez sur l&apos;icône d&apos;installation dans la barre d&apos;adresse (à droite de l&apos;URL)
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-semibold text-gray-900 mb-2">Firefox</h4>
                    <p className="text-gray-600 text-sm">
                      Firefox ne supporte pas les PWA. Utilisez Chrome ou Edge.
                    </p>
                  </div>
                </div>

                {deferredPrompt && (
                  <div className="text-center pt-4">
                    <Button
                      onClick={handleInstall}
                      className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-full px-8 py-6 text-lg font-semibold"
                    >
                      <Download className="w-6 h-6 mr-2" />
                      Installer l&apos;application
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6"
          >
            Continuer sur le site web
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 py-6 border-t border-white/10">
        <p className="text-center text-blue-200 text-sm">
          © 2025 WÔMA de ARTEKNOS - Dakar, Sénégal 🇸🇳
        </p>
      </footer>
    </div>
  );
}
